import type { ScaleSummary } from '@classgo/shared';

import type { ScoreIssue } from '../../gradebook/types';

/**
 * The precision of a scale: its decimals, or 1 when the API did not say (the demo's summary).
 *
 * @param {Pick<ScaleSummary, 'decimals'> | null | undefined} scale -- The scale.
 * @returns The decimals.
 */
export const decimalsOf = (scale: { decimals?: number } | null | undefined): number =>
  scale?.decimals ?? 1;

/**
 * A score as the screen prints it: es-CO digits with the scale's decimals, or "—" when there is
 * none.
 *
 * @param {number | null | undefined} value -- The score.
 * @param {number} decimals -- The scale's decimals.
 * @returns The text.
 */
export const formatScore = (value: number | null | undefined, decimals: number): string =>
  value === null || value === undefined
    ? '—'
    : value.toLocaleString('es-CO', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

/**
 * Parses a typed score ("3,5" or "3.5"); null when the box is empty, NaN when malformed.
 *
 * @param {string} text -- What was typed.
 * @returns The score.
 */
export const parseScore = (text: string): number | null =>
  text.trim() === '' ? null : Number(text.trim().replace(',', '.'));

/**
 * Why a typed score would be refused, checked as the API does (`docs/features/grades.md`):
 * within the scale and with no more decimals than it uses. Null when it can be sent, also when
 * the box is empty (an empty box clears the score).
 *
 * @param {string} text -- What was typed.
 * @param {ScaleSummary | null} scale -- The year's scale, when known.
 * @returns The issue, or null.
 */
export const scoreIssue = (
  text: string,
  scale: Pick<ScaleSummary, 'minScore' | 'maxScore' | 'decimals'> | null,
): ScoreIssue => {
  const score = parseScore(text);
  if (score === null) return null;
  if (Number.isNaN(score)) return 'invalid';
  if (!scale) return null;
  if (score < scale.minScore || score > scale.maxScore) return 'range';
  const decimals = decimalsOf(scale);
  const shifted = score * 10 ** decimals;
  if (Math.abs(shifted - Math.round(shifted)) > 1e-9) return 'decimals';
  return null;
};

/**
 * A student's name as the grade book shows it, family name first.
 *
 * @param {object} row -- The names.
 * @returns The name.
 */
export const studentName = (row: { givenName: string | null; familyName: string | null }): string =>
  [row.familyName, row.givenName].filter(Boolean).join(', ');

/**
 * A student's name in reading order, for a dialog's title ("Ajustar la nota de Valentina Gómez").
 *
 * @param {object} row -- The names.
 * @returns The name.
 */
export const readingName = (row: { givenName: string | null; familyName: string | null }): string =>
  [row.givenName, row.familyName].filter(Boolean).join(' ');

/**
 * The index of the box to focus after a key in the score grid: Enter and the down arrow move
 * down the column, Shift+Enter and the up arrow move up; null for any other key.
 *
 * @param {object} key -- The key and whether Shift was held.
 * @param {number} index -- The row of the box that got the key.
 * @param {number} count -- How many rows the grid has.
 * @returns The row to focus, or null.
 */
export const nextRowFor = (
  key: { key: string; shiftKey: boolean },
  index: number,
  count: number,
): number | null => {
  const down = key.key === 'ArrowDown' || (key.key === 'Enter' && !key.shiftKey);
  const up = key.key === 'ArrowUp' || (key.key === 'Enter' && key.shiftKey);
  if (!down && !up) return null;
  const target = index + (down ? 1 : -1);
  return target >= 0 && target < count ? target : index;
};

/**
 * The computed grade of a row: the weighted average before any override, or the period grade
 * itself when the API did not send one apart (the demo's book).
 *
 * @param {object} row -- The row.
 * @returns The computed grade.
 */
export const computedOf = (row: {
  computedGrade?: number | null;
  periodGrade: number | null;
}): number | null => (row.computedGrade === undefined ? row.periodGrade : row.computedGrade);
